/**
 * RoofListItem - Individual roof item in the roof list
 *
 * Displays roof information (ID, area, modules) with active state and delete action.
 */

import React from 'react';
import { Home as HomeIcon, X } from 'lucide-react';

interface RoofListItemProps {
  roofId: number;
  isActive: boolean;
  area: number;
  moduleCount: number;
  onSelect: () => void;
  onRemove: () => void;
}

export function RoofListItem({
  roofId,
  isActive,
  area,
  moduleCount,
  onSelect,
  onRemove,
}: RoofListItemProps) {
  return (
    <div
      onClick={onSelect}
      className={`
        p-3 rounded-lg border-2 cursor-pointer transition-all
        ${isActive
          ? 'bg-[#EBFD03]/30 border-[#EBFD03] shadow-md'
          : 'bg-gray-50 border-gray-300 hover:border-gray-400 hover:bg-gray-100'
        }
      `}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <HomeIcon className="h-4 w-4 text-gray-700" />
            <span className={`text-sm font-semibold ${isActive ? 'text-black' : 'text-gray-700'}`}>
              Roof #{roofId}
            </span>
            {isActive && (
              <span className="text-xs bg-[#EBFD03] text-black px-2 py-0.5 rounded-full font-semibold">
                ACTIVE
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {area > 0 ? `${area.toFixed(1)} m²` : 'N/A'}
          </div>
          {moduleCount > 0 && (
            <div className="text-xs text-green-600 mt-0.5 font-semibold">
              {moduleCount} modules placed
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
          title="Remove from roof list"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default RoofListItem;
